import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { fmtCupo } from '../../utils/format';
import VolverOnboardingBtn from '../../components/VolverOnboardingBtn';
import { toast } from 'sonner';
import { Calendar, Users, Check, X, ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, AlertTriangle, Clock, MapPin, Video, ExternalLink, Search, Share2, Copy, Link2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

export default function EseSesiones() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const empresaId = user?.empresa_colaboradora_id;

  const [sesiones, setSesiones] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadoresElegibles, setTrabajadoresElegibles] = useState(null); // null = aún no cargado para la sesión actual
  const [loadingElegibles, setLoadingElegibles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [showDetalle, setShowDetalle] = useState(null);

  const [showInscribir, setShowInscribir] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');

  const [confirmCancel, setConfirmCancel] = useState(null);
  const [shareModal, setShareModal] = useState(null); // sesion to share link from
  const [conflictoInscribir, setConflictoInscribir] = useState(null); // {message, conflictos, sesion}

  const fetchSesiones = useCallback(async () => {
    if (!empresaId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sesiones/disponibles/ese?empresa_id=${empresaId}`, { headers });
      // Ordenar por fecha DESC (más futura arriba). Empatar por hora_inicio DESC.
      const sorted = [...(res.data || [])].sort((a, b) => {
        const fa = a.fecha || '';
        const fb = b.fecha || '';
        if (fa !== fb) return fb.localeCompare(fa);
        return (b.hora_inicio || '').localeCompare(a.hora_inicio || '');
      });
      setSesiones(sorted);
    } catch { }
    setLoading(false);
  }, [token, empresaId]);

  const fetchTrabajadores = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ese/trabajadores`, { headers });
      setTrabajadores(res.data || []);
    } catch { }
  }, [token]);

  useEffect(() => { fetchSesiones(); fetchTrabajadores(); }, [fetchSesiones, fetchTrabajadores]);

  const loadInscripciones = async (sesionId) => {
    try {
      const res = await axios.get(`${API}/sesiones/${sesionId}/inscripciones`, { headers });
      return res.data;
    } catch { return []; }
  };

  const openDetalle = async (sesion) => {
    const inscs = await loadInscripciones(sesion.id);
    setShowDetalle({ ...sesion, _inscripciones: inscs });
  };

  const openInscribir = async (sesion) => {
    let inscs = sesion._inscripciones;
    if (!inscs) {
      inscs = await loadInscripciones(sesion.id);
    }
    setShowInscribir({ ...sesion, _inscripciones: inscs });
    setSelectedWorkers([]);
    setWorkerSearch('');
    // Cargar trabajadores elegibles (sin curso vigente / sin inscripción pendiente en otra sesión)
    setTrabajadoresElegibles(null);
    setLoadingElegibles(true);
    try {
      const res = await axios.get(`${API}/ese/sesiones/${sesion.id}/trabajadores-elegibles`, { headers });
      setTrabajadoresElegibles(res.data || []);
    } catch {
      // Fallback: usar todos los trabajadores
      setTrabajadoresElegibles(trabajadores);
    }
    setLoadingElegibles(false);
  };

  const toggleWorker = (w) => {
    setSelectedWorkers(prev =>
      prev.find(s => s.rut === w.rut)
        ? prev.filter(s => s.rut !== w.rut)
        : [...prev, { rut: w.rut, nombre: w.nombre_completo }]
    );
  };

  const handleInscribir = async (liberarPrevias = false) => {
    if (!selectedWorkers.length) return;
    try {
      await axios.post(`${API}/sesiones/inscribir`, {
        sesion_id: showInscribir.id,
        empresa_id: empresaId,
        trabajadores: selectedWorkers,
        liberar_previas: liberarPrevias,
      }, { headers });
      const inscritosCount = selectedWorkers.length;
      const sesionForShare = { ...showInscribir };
      toast.success(liberarPrevias
        ? `${inscritosCount} inscrito(s) · inscripciones previas liberadas`
        : `${inscritosCount} trabajador(es) inscrito(s)`);
      setShowInscribir(null);
      setConflictoInscribir(null);
      await fetchSesiones();
      if (showDetalle) {
        const inscs = await loadInscripciones(showDetalle.id);
        setShowDetalle(prev => prev ? { ...prev, _inscripciones: inscs } : null);
      }
      setShareModal({ sesion: sesionForShare, count: inscritosCount });
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 409 con conflicto de inscripción en otra sesión del mismo curso → abrir modal
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'worker_enrolled_in_other_session') {
        setConflictoInscribir({ ...detail, sesion: showInscribir });
        return;
      }
      const msg = (detail && typeof detail === 'object')
        ? (detail.message || 'No es posible inscribir. Verifique los conflictos o el corte de inscripción.')
        : (detail || 'Error al inscribir');
      toast.error(msg);
    }
  };

  const copyShareLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar el link');
    }
  };

  const handleCancelInscripcion = async (inscId) => {
    try {
      await axios.delete(`${API}/sesiones/inscripcion/${inscId}`, { headers });
      toast.success('Inscripcion cancelada');
      setConfirmCancel(null);
      await fetchSesiones();
      if (showDetalle) {
        const inscs = await loadInscripciones(showDetalle.id);
        setShowDetalle(prev => prev ? { ...prev, _inscripciones: inscs } : null);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (detail && typeof detail === 'object')
        ? (detail.message || 'Error al cancelar')
        : (detail || 'Error al cancelar');
      toast.error(msg);
    }
  };

  // Calendar
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const getSesionesForDay = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return sesiones.filter(s => s.fecha === dateStr);
  };

  const baseWorkers = trabajadoresElegibles ?? trabajadores;
  const filteredWorkers = baseWorkers.filter(w => {
    const q = workerSearch.toLowerCase();
    if (!q) return true;
    const rutClean = (w.rut || '').replace(/[.\-]/g, '').toLowerCase();
    return w.nombre_completo?.toLowerCase().includes(q) || rutClean.includes(q.replace(/[.\-]/g, ''));
  });

  // Mis inscritos in detalle
  const allInscripciones = showDetalle?._inscripciones || [];
  const misInscritos = allInscripciones.filter(i => i.empresa_id === empresaId);

  // Hoy en zona Chile (yyyy-mm-dd) para identificar sesiones pasadas/cerradas
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const isPast = (s) => s?.fecha && s.fecha < todayStr;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ese-sesiones">
      <div className="flex items-center gap-3">
        <VolverOnboardingBtn user={user} />
        <h1 className="text-2xl font-bold text-slate-900">Capacitaciones</h1>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <button onClick={() => setCurrentMonth(new Date(year, month - 1))} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="prev-month">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-slate-800">{MONTHS[month]} {year}</h2>
          <button onClick={() => setCurrentMonth(new Date(year, month + 1))} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="next-month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7">
          {DAYS.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2 border-b border-slate-100">{d}</div>
          ))}
          {days.map((d, i) => {
            const daySesiones = d ? getSesionesForDay(d) : [];
            const isToday = d && new Date().getDate() === d && new Date().getMonth() === month && new Date().getFullYear() === year;
            return (
              <div key={i} className={`min-h-[90px] border-b border-r border-slate-100 p-1 ${d ? 'bg-white' : 'bg-slate-50/50'}`}>
                {d && (
                  <>
                    <span className={`text-xs font-medium ${isToday ? 'bg-blue-500 text-white px-1.5 py-0.5 rounded-full' : 'text-slate-500'}`}>{d}</span>
                    <div className="mt-1 space-y-0.5">
                      {daySesiones.map(s => {
                        const past = isPast(s);
                        return (
                        <div key={s.id} onClick={() => openDetalle(s)}
                          className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer truncate ${
                            past ? 'bg-slate-200 text-slate-500 line-through hover:bg-slate-300'
                            : s.cerrada_para_ese ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                            : s.cupo_disponible === 0 ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={
                            past
                              ? `${s.capacitacion_nombre} (${s.inscritos}/${fmtCupo(s.cupo_maximo)}) - Cerrada`
                              : s.cerrada_para_ese
                                ? `${s.capacitacion_nombre} · Inscripción ESE cerrada (corte ${s.cierre_ese_dias_antes} día(s) antes a las ${s.cierre_ese_hora})`
                                : `${s.capacitacion_nombre} (${s.inscritos}/${fmtCupo(s.cupo_maximo)})`
                          }>
                          {s.capacitacion_nombre?.substring(0, 18)} {s.inscritos}/{fmtCupo(s.cupo_maximo)}
                        </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Sesiones list */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3">Sesiones Disponibles</h3>
        {sesiones.length === 0 ? (
          <p className="text-sm text-slate-400">No hay sesiones programadas</p>
        ) : (
          <div className="space-y-2">
            {sesiones.map(s => {
              const past = isPast(s);
              return (
              <div key={s.id} onClick={() => openDetalle(s)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  past ? 'border-slate-200 bg-slate-50 opacity-70 hover:bg-slate-100'
                  : 'border-slate-100 hover:border-blue-200 hover:bg-blue-50/30'}`}>
                <div>
                  <p className={`font-medium ${past ? 'text-slate-500' : 'text-slate-800'}`}>
                    {toTitleCase(s.capacitacion_nombre)}
                    {past && <span className="ml-2 text-[10px] font-bold uppercase bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded">Cerrada</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.fecha.split('-').reverse().join('/')} | {s.hora_inicio} - {s.hora_fin} | {s.lugar}
                    {s.url_sesion && (
                      <a href={s.url_sesion} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="ml-2 text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5">
                        <Video className="w-3 h-3" /><ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${s.cupo_disponible === 0 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    <Users className="w-3 h-3 inline mr-1" />{s.inscritos}/{fmtCupo(s.cupo_maximo)}
                  </span>
                  {s.max_por_ese != null && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      (s.disponibles_ese || 0) === 0 ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'
                    }`} title={`Tu empresa puede inscribir hasta ${s.max_por_ese} trabajadores por sesión (regla del Capacitador)`}
                       data-testid={`badge-ese-cupo-${s.id}`}>
                      Tú: {s.mis_inscritos_count || 0}/{s.max_por_ese}
                    </span>
                  )}
                  {s.cerrada_para_ese && !past && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-rose-100 text-rose-800"
                      title={`Corte ESE: ${s.cierre_ese_dias_antes} día(s) antes a las ${s.cierre_ese_hora} (Chile)`}
                      data-testid={`badge-ese-cerrada-${s.id}`}>
                      Inscripción cerrada
                    </span>
                  )}
                  {!past && (
                    <button onClick={(e) => { e.stopPropagation(); if (!s.cerrada_para_ese) openInscribir(s); }}
                      disabled={(s.disponibles_ese || 0) === 0 || s.cerrada_para_ese}
                      className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        s.cerrada_para_ese
                          ? `Inscripciones ESE cerradas. Corte fue ${s.cierre_ese_dias_antes} día(s) antes a las ${s.cierre_ese_hora}. Contacte al Capacitador para excepciones.`
                          : (s.disponibles_ese || 0) === 0
                            ? `Tu empresa alcanzó el máximo de ${s.max_por_ese} inscritos`
                            : ''
                      }
                      data-testid={`inscribir-btn-${s.id}`}>
                      <Users className="w-3 h-3 inline mr-1" />Inscribir
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Detalle Sesion */}
      {showDetalle && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowDetalle(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{toTitleCase(showDetalle.capacitacion_nombre)}</h2>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{showDetalle.fecha.split('-').reverse().join('/')}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{showDetalle.hora_inicio} - {showDetalle.hora_fin}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{showDetalle.lugar}</span>
                </div>
              </div>
              <button onClick={() => setShowDetalle(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${showDetalle.cupo_disponible === 0 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {showDetalle.inscritos}/{fmtCupo(showDetalle.cupo_maximo)} inscritos totales
                  </span>
                  {showDetalle.max_por_ese != null && (
                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                      (showDetalle.disponibles_ese || 0) === 0 ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'
                    }`} title={`Regla: cada empresa puede inscribir hasta ${showDetalle.max_por_ese} trabajadores por sesión`}
                       data-testid="badge-cupo-ese">
                      Tu empresa: {showDetalle.mis_inscritos_count || 0}/{showDetalle.max_por_ese}
                    </span>
                  )}
                </div>
                {isPast(showDetalle) ? (
                  <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-700 px-2 py-1 rounded">Sesión Cerrada</span>
                ) : showDetalle.cerrada_para_ese ? (
                  <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-2 py-1 rounded"
                    title={`Corte ESE: ${showDetalle.cierre_ese_dias_antes} día(s) antes a las ${showDetalle.cierre_ese_hora}`}
                  >Inscripción Cerrada</span>
                ) : (
                  <button onClick={() => openInscribir(showDetalle)}
                    disabled={(showDetalle.disponibles_ese || 0) === 0}
                    className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={(showDetalle.disponibles_ese || 0) === 0 ? `Tu empresa alcanzó el máximo de ${showDetalle.max_por_ese} inscritos en esta sesión` : ''}
                    data-testid="inscribir-from-detail">
                    <Users className="w-3 h-3 inline mr-1" />Inscribir Trabajadores
                  </button>
                )}
              </div>

              {misInscritos.length > 0 ? (
                <div>
                  {/* Link a compartir con los trabajadores */}
                  <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50/60" data-testid="share-link-section">
                    <div className="flex items-center gap-2 mb-2">
                      <Share2 className="w-4 h-4 text-blue-600" />
                      <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Link para compartir con tus trabajadores</p>
                    </div>
                    {showDetalle.url_sesion ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 px-2.5 py-2 bg-white border border-blue-200 rounded-md min-w-0">
                          <Link2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs text-slate-700 truncate font-mono" title={showDetalle.url_sesion}>{showDetalle.url_sesion}</span>
                        </div>
                        <button onClick={() => copyShareLink(showDetalle.url_sesion)}
                          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                          data-testid="copy-share-link-btn">
                          <Copy className="w-3.5 h-3.5" />Copiar
                        </button>
                        <a href={showDetalle.url_sesion} target="_blank" rel="noopener noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-white border border-blue-200 text-blue-700 text-xs rounded-md hover:bg-blue-100 transition-colors"
                          data-testid="open-share-link-btn">
                          <ExternalLink className="w-3.5 h-3.5" />Abrir
                        </a>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 italic">Esta sesión aún no tiene link disponible. Contacta al Capacitador.</p>
                    )}
                  </div>

                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Mis Inscritos ({misInscritos.length})</p>
                  <div className="space-y-1">
                    {misInscritos.map(ins => (
                      <div key={ins.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          <span className="text-sm text-slate-700">{toTitleCase(ins.nombre_trabajador)}</span>
                          <span className="text-xs text-slate-400 font-mono">{formatRut(ins.rut_trabajador)}</span>
                        </div>
                        <button onClick={() => setConfirmCancel(ins)}
                          className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                          data-testid={`cancel-${ins.id}`}>
                          Cancelar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No tiene trabajadores inscritos en esta sesion</p>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Inscribir */}
      {showInscribir && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowInscribir(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Inscribir Trabajadores</h2>
                <p className="text-sm text-slate-500">{toTitleCase(showInscribir.capacitacion_nombre)} - {showInscribir.fecha.split('-').reverse().join('/')}</p>
              </div>
              <button onClick={() => setShowInscribir(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 pt-3 pb-2 border-b border-slate-100 bg-amber-50/40">
              <p className="text-[11px] text-amber-800/80 leading-snug">
                Solo se muestran trabajadores <strong>sin este curso vigente</strong> y que no estén inscritos en otra sesión pendiente del mismo curso.
              </p>
              {showInscribir.max_por_ese != null && (
                <p className="text-[11px] text-teal-800 mt-1.5 leading-snug" data-testid="regla-cupo-empresa">
                  <strong>Regla del Capacitador:</strong> tu empresa puede inscribir hasta {showInscribir.max_por_ese} trabajadores por sesión.{' '}
                  Ya llevas <strong>{showInscribir.mis_inscritos_count || 0}</strong> · disponibles: <strong className={(showInscribir.disponibles_ese || 0) === 0 ? 'text-red-600' : 'text-emerald-600'}>{showInscribir.disponibles_ese || 0}</strong>.
                </p>
              )}
            </div>

            <div className="px-6 py-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input type="text" value={workerSearch} onChange={e => setWorkerSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT..."
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm" data-testid="worker-search" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-3">
              {loadingElegibles ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : filteredWorkers.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8" data-testid="no-elegibles-msg">
                  {workerSearch ? 'No se encontraron trabajadores' : 'No hay trabajadores elegibles para este curso. Todos lo tienen vigente o ya están inscritos en otra sesión pendiente.'}
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredWorkers.slice(0, 50).map(w => {
                    const currentInscs = showInscribir?._inscripciones || showDetalle?._inscripciones || [];
                    const alreadyInscribed = currentInscs.some(i => {
                      const iRut = (i.rut_trabajador || '').replace(/[.\-]/g, '');
                      const wRut = (w.rut || '').replace(/[.\-]/g, '');
                      return iRut === wRut;
                    });
                    const selected = selectedWorkers.some(s => s.rut === w.rut);
                    return (
                      <div key={w.rut}
                        onClick={() => !alreadyInscribed && toggleWorker(w)}
                        className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${
                          alreadyInscribed ? 'bg-green-50 cursor-not-allowed opacity-60' :
                          selected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
                        }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                            alreadyInscribed ? 'border-green-400 bg-green-400' :
                            selected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                          }`}>
                            {(selected || alreadyInscribed) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{toTitleCase(w.nombre_completo)}</p>
                            <p className="text-xs text-slate-400">{formatRut(w.rut)} {w.cargo ? `- ${toTitleCase(w.cargo)}` : ''}</p>
                          </div>
                        </div>
                        {alreadyInscribed && <span className="text-[10px] text-green-600 font-medium">Inscrito</span>}
                      </div>
                    );
                  })}
                  {filteredWorkers.length > 50 && (
                    <p className="text-xs text-slate-400 text-center py-2">Mostrando 50 de {filteredWorkers.length}. Use el buscador para filtrar.</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <span className="text-sm text-slate-500">
                {selectedWorkers.length} seleccionado(s)
                {showInscribir.max_por_ese != null && selectedWorkers.length > (showInscribir.disponibles_ese || 0) && (
                  <span className="ml-2 text-red-600 font-semibold">
                    · Excede disponible ({showInscribir.disponibles_ese || 0})
                  </span>
                )}
              </span>
              <button onClick={() => handleInscribir(false)}
                disabled={selectedWorkers.length === 0 || (showInscribir.max_por_ese != null && selectedWorkers.length > (showInscribir.disponibles_ese || 0))}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="confirmar-inscripcion-btn">
                <Check className="w-4 h-4" />Inscribir ({selectedWorkers.length})
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Confirmar Cancelacion */}
      {confirmCancel && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmCancel(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Cancelar Inscripcion</h3>
                <p className="text-sm text-slate-500">Se liberara el cupo de {toTitleCase(confirmCancel.nombre_trabajador)}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmCancel(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">No</button>
              <button onClick={() => handleCancelInscripcion(confirmCancel.id)}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-cancel-btn">
                Si, cancelar
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Compartir Link (post-inscripcion) */}
      {shareModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={ev => ev.stopPropagation()} data-testid="share-link-modal">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Share2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-slate-900">¡Inscripción exitosa!</h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Inscribiste a <span className="font-semibold">{shareModal.count}</span> trabajador(es) en <span className="font-medium">{toTitleCase(shareModal.sesion.capacitacion_nombre)}</span>.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5" />Comparte este link con tus trabajadores
                </p>
                {shareModal.sesion.url_sesion ? (
                  <>
                    <div className="flex items-center gap-2 px-2.5 py-2 bg-white border border-blue-200 rounded-md mb-2">
                      <span className="text-xs text-slate-700 truncate font-mono flex-1" title={shareModal.sesion.url_sesion}>{shareModal.sesion.url_sesion}</span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => copyShareLink(shareModal.sesion.url_sesion)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                        data-testid="share-modal-copy-btn">
                        <Copy className="w-3.5 h-3.5" />Copiar link
                      </button>
                      <a href={shareModal.sesion.url_sesion} target="_blank" rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-blue-200 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100 transition-colors"
                        data-testid="share-modal-open-btn">
                        <ExternalLink className="w-3.5 h-3.5" />Abrir
                      </a>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-slate-600 italic">Esta sesión aún no tiene link disponible. Contacta al Capacitador para obtenerlo.</p>
                )}
              </div>
            </div>
            <div className="flex justify-end px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setShareModal(null)}
                className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-900 transition-colors"
                data-testid="share-modal-close-btn">
                Entendido
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Conflicto: trabajador ya inscrito en otra sesión del mismo curso (a futuro) */}
      {conflictoInscribir && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="modal-conflicto-inscribir">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-base font-semibold text-slate-900">Ya inscrito en otra sesión</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">{conflictoInscribir.message}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {conflictoInscribir.conflictos?.map((c) => (
                  <div key={c.inscripcion_id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <p className="text-sm font-medium text-slate-800">{c.nombre || c.rut}</p>
                    <p className="text-xs text-slate-500 font-mono">{c.rut}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-slate-700">
                        Sesión del <strong>{c.fecha_sesion ? c.fecha_sesion.split('-').reverse().join('-') : '—'}</strong>
                      </span>
                      {c.nomina_cerrada && <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">Nómina cerrada</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-xs text-teal-900">
                <strong>Al confirmar</strong>, se liberará la inscripción anterior y se procederá a inscribir en esta nueva sesión.
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setConflictoInscribir(null)}
                className="px-4 h-9 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                data-testid="btn-conflicto-cancelar"
              >Cancelar</button>
              <button
                onClick={() => handleInscribir(true)}
                className="px-4 h-9 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 inline-flex items-center gap-1.5"
                data-testid="btn-conflicto-liberar"
              >
                Liberar de solicitudes anteriores
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
